import { createContext, useContext, useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import {
  answerInterview,
  clarifyInterview,
  endInterview as endInterviewApi,
  fetchInterview,
  InterviewApiError,
  nextInterview,
  regenerateInterview,
  startInterview,
} from './interviewService';
import { parseExtract } from './interviewSchema';
import { loadLastQuestionId, saveLastQuestionId } from './interviewStorage';
import type {
  InterviewExtract,
  InterviewOpResult,
  InterviewSession,
  InterviewStartRequest,
} from '../types/interview';
import { EMPTY_EXTRACT } from '../types/interview';

export type Step =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; question: string; questionId?: string }
  | { kind: 'done' }
  | { kind: 'failed'; message: string };

export type SendError = { kind: 'update' | 'next' | 'nextQuestion' | 'clarify' | 'start' | 'regenerate' | 'planLimit'; message: string } | null;

export interface InterviewContextValue {
  session: InterviewSession | null;
  extract: InterviewExtract;
  workingDoc: string;
  step: Step;
  error: SendError;
  clarification: string | null;
  sending: boolean;
  clarifying: boolean;
  regenerating: boolean;
  recovering: boolean;
  start: (req: InterviewStartRequest) => Promise<InterviewOpResult | null>;
  load: (sessionId: string) => Promise<void>;
  answer: (text: string, direction?: string) => Promise<InterviewOpResult | null>;
  clarify: () => Promise<void>;
  redo: (direction?: string) => Promise<InterviewOpResult | null>;
  next: () => Promise<InterviewOpResult | null>;
  endSession: (reason?: 'complete' | 'abandoned') => Promise<void>;
  setDirection: (direction: string) => void;
  clearError: () => void;
}

const InterviewContext = createContext<InterviewContextValue | null>(null);

function errorFromResult(result: InterviewOpResult): SendError {
  if (!result.failedStep) return null;
  return { kind: result.failedStep, message: result.error || 'Operation failed' };
}

function errorFromException(err: unknown): NonNullable<SendError> {
  if (err instanceof InterviewApiError) {
    if (err.code === 'PLAN_LIMIT' || err.code === 'UPGRADE_REQUIRED') {
      return { kind: 'planLimit', message: err.message };
    }
    return { kind: 'start', message: err.message };
  }
  const message = err instanceof Error ? err.message : 'Operation failed';
  return { kind: 'start', message };
}
export function InterviewProvider({
  sessionId: initialSessionId,
  children,
}: {
  sessionId?: string;
  children: ReactNode;
}) {
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [workingDoc, setWorkingDoc] = useState<string>('');
  const [extract, setExtract] = useState<InterviewExtract>(EMPTY_EXTRACT);
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [error, setError] = useState<SendError>(null);
  const [clarification, setClarification] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [clarifying, setClarifying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [direction, setDirection] = useState<string>('');

  const applySession = useCallback((next: InterviewSession) => {
    setSession(next);
    setWorkingDoc(next.workingDoc || '');
    setExtract(parseExtract(next.extract));
    setDirection(next.direction || '');
  }, []);

  const load = useCallback(
    async (sessionId: string) => {
      setStep({ kind: 'loading' });
      setError(null);
      setClarification(null);
      try {
        const { session: loaded, transcript } = await fetchInterview(sessionId);
        applySession(loaded);
        if (loaded.status === 'complete' || loaded.status === 'abandoned') {
          setStep({ kind: 'done' });
          return;
        }
        // Determine the current step from the transcript: the most recent
        // assistant question that is NOT yet followed by a user answer.
        const lastQuestion = [...transcript].reverse().find((m) => m.role === 'assistant' && m.kind === 'question');
        const lastUserAnswer = [...transcript].reverse().find((m) => m.role === 'user' && m.kind === 'answer');
        if (lastQuestion && (!lastUserAnswer || lastUserAnswer.createdAt < lastQuestion.createdAt)) {
          setStep({ kind: 'ready', question: lastQuestion.content, questionId: lastQuestion.id });
          saveLastQuestionId(loaded.id, lastQuestion.id);
          return;
        }
        // The user answered the last question but the next question was never
        // produced (the interview was closed mid-flight). Recover by asking
        // the server to produce the next question from the existing transcript.
        if (lastUserAnswer) {
          setRecovering(true);
          try {
            const result = await nextInterview(loaded.id);
            if (result.session) applySession(result.session);
            if (result.workingDoc) setWorkingDoc(result.workingDoc);
            if (result.extract) setExtract(parseExtract(result.extract));
            if (result.isDone) {
              setStep({ kind: 'done' });
            } else if (result.question) {
              setStep({ kind: 'ready', question: result.question });
            } else {
              setStep({ kind: 'idle' });
            }
            if (result.failedStep) setError(errorFromResult(result));
          } catch (err) {
            setError(errorFromException(err));
            setStep({ kind: 'idle' });
          } finally {
            setRecovering(false);
          }
          return;
        }
        // No question and no answer — empty transcript edge case. Just idle.
        setStep({ kind: 'idle' });
      } catch (err) {
        setError(errorFromException(err));
        setStep({ kind: 'idle' });
      }
    },
    [applySession]
  );

  useEffect(() => {
    if (initialSessionId) {
      void load(initialSessionId);
    }
  }, [initialSessionId, load]);

  const start = useCallback(
    async (req: InterviewStartRequest) => {
      setError(null);
      setClarification(null);
      setStep({ kind: 'loading' });
      try {
        const result = await startInterview(req);
        if (result.session) applySession(result.session);
        if (result.isDone) {
          setStep({ kind: 'done' });
        } else if (result.question) {
          setStep({ kind: 'ready', question: result.question });
        } else {
          setStep({ kind: 'failed', message: result.error || 'Could not start the interview.' });
        }
        if (result.failedStep) setError(errorFromResult(result));
        return result;
      } catch (err) {
        const e = errorFromException(err);
        setError(e);
        setStep({ kind: 'failed', message: e.message });
        return null;
      }
    },
    [applySession]
  );

  const answer = useCallback(
    async (text: string, overrideDirection?: string) => {
      if (!session) return null;
      setSending(true);
      setError(null);
      setClarification(null);
      try {
        const result = await answerInterview({
          sessionId: session.id,
          text,
          direction: overrideDirection ?? direction,
        });
        if (result.session) applySession(result.session);
        if (result.workingDoc) setWorkingDoc(result.workingDoc);
        if (result.extract) setExtract(parseExtract(result.extract));
        if (result.isDone) {
          setStep({ kind: 'done' });
        } else if (result.question) {
          setStep({ kind: 'ready', question: result.question });
        }
        if (result.failedStep) setError(errorFromResult(result));
        return result;
      } catch (err) {
        setError(errorFromException(err));
        return null;
      } finally {
        setSending(false);
      }
    },
    [session, direction, applySession]
  );

  const clarify = useCallback(async () => {
    if (!session) return;
    setClarifying(true);
    setError(null);
    try {
      const lastQId = loadLastQuestionId(session.id);
      const result = await clarifyInterview({ sessionId: session.id, questionId: lastQId || undefined });
      if (result.clarification) {
        setClarification(result.clarification);
      }
      if (result.failedStep) setError(errorFromResult(result));
    } catch (err) {
      setError(errorFromException(err));
    } finally {
      setClarifying(false);
    }
  }, [session]);

  const redo = useCallback(
    async (overrideDirection?: string) => {
      if (!session) return null;
      setRegenerating(true);
      setError(null);
      try {
        const result = await regenerateInterview({
          sessionId: session.id,
          direction: overrideDirection ?? direction,
        });
        if (result.session) applySession(result.session);
        if (result.workingDoc) setWorkingDoc(result.workingDoc);
        if (result.extract) setExtract(parseExtract(result.extract));
        if (result.failedStep) setError(errorFromResult(result));
        return result;
      } catch (err) {
        setError(errorFromException(err));
        return null;
      } finally {
        setRegenerating(false);
      }
    },
    [session, direction, applySession]
  );

  const next = useCallback(async () => {
    if (!session) return null;
    setRecovering(true);
    setError(null);
    try {
      const result = await nextInterview(session.id, direction);
      if (result.session) applySession(result.session);
      if (result.workingDoc) setWorkingDoc(result.workingDoc);
      if (result.extract) setExtract(parseExtract(result.extract));
      if (result.isDone) {
        setStep({ kind: 'done' });
      } else if (result.question) {
        setStep({ kind: 'ready', question: result.question });
      }
      if (result.failedStep) setError(errorFromResult(result));
      return result;
    } catch (err) {
      setError(errorFromException(err));
      return null;
    } finally {
      setRecovering(false);
    }
  }, [session, direction, applySession]);

  const endSession = useCallback(
    async (reason: 'complete' | 'abandoned' = 'complete') => {
      if (!session) return;
      try {
        const result = await endInterviewApi(session.id, reason);
        if (result.session) applySession(result.session);
        setStep({ kind: 'done' });
      } catch (err) {
        setError(errorFromException(err));
      }
    },
    [session, applySession]
  );

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<InterviewContextValue>(
    () => ({
      session,
      extract,
      workingDoc,
      step,
      error,
      clarification,
      sending,
      clarifying,
      regenerating,
      recovering,
      start,
      load,
      answer,
      clarify,
      redo,
      next,
      endSession,
      setDirection,
      clearError,
    }),
    [session, extract, workingDoc, step, error, clarification, sending, clarifying, regenerating, recovering, start, load, answer, clarify, redo, next, endSession, clearError]
  );

  return <InterviewContext.Provider value={value}>{children}</InterviewContext.Provider>;
}

export function useInterview(): InterviewContextValue {
  const ctx = useContext(InterviewContext);
  if (!ctx) throw new Error('useInterview must be used inside InterviewProvider');
  return ctx;
}
